import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';

import { message } from 'antd';
import { get } from 'lodash';
import { v4 as uuid } from 'uuid';

import {
  updateSectionContentAtPath,
  updateSectionTitleAtPath,
  addOptionalSection as addOptionalSectionToData,
  addPackagingBlock,
  isBlockPath,
  nextBlockOrder,
  nextCustomSectionOrder,
  removePackagingBlock,
  updateNatureTypeAtPath,
  sectionsPath,
  addCustomSection as addCustomSectionToData,
  removeOptionalSection,
  removeCustomSection,
  moveCustomSection as moveCustomSectionInData,
  addPredefinedSection as addPredefinedSectionToData,
  keepCustomSection as keepCustomSectionInData,
  insertPackagingBlockAfter,
} from '../utils/sectionTree';
import {
  addIdmpsToComposition,
  removeIdmpFromComposition,
  cloneCompositionForIdmps,
  mergeCompositionIntoSibling as mergeCompositionIntoSiblingOp,
  isTitleTaken,
  peelIdmpsFromComposition,
  pickIdmpRefs,
} from '../utils/compositionOps';
import {
  buildCompositionSectionTree,
  buildEmptyComposition,
  buildEmptyPackagingBlock,
  buildEmptySection,
  findSectionNode,
  getCustomSectionDefaults,
  getPredefinedEntries,
  getSchemaEntryAtPath,
  resolveSectionDef,
} from '../utils/schemaUtils';

import type {
  EpiComposition,
  EpiCustomSection,
  EpiPackagingBlock,
  EpiPredefinedEntry,
  EpiText,
  EpiDocumentData,
  EpiSectionNode,
} from '../types/types';
import type { IEpiContext, IEpiProviderProps } from './types';
import { TAB_CONFIGS } from '../types/constants';
import {
  emptyText,
  normalizeForSave,
  setText,
  text,
} from '../utils/epiHelpers';

// global in-memory storage to replace local storage - each document has its own storage space
const globalCompositionsStorage: Record<
  string,
  Record<string, EpiComposition[]>
> = {};

/**
 * The compositions currently held for a document, across every tab. Reading
 * this is how the document-level UI (which lives above the per-tab providers)
 * sees edits that haven't been saved yet.
 */
export const getStoredCompositions = (
  documentId: string
): Record<string, EpiComposition[]> =>
  globalCompositionsStorage[documentId] ?? {};

/**
 * Drops a document's cached compositions so the next provider mount seeds from
 * the instance again. Needed after the screen refetches a saved instance: the
 * cache holds the client-side ids we sent, and the server has since assigned
 * its own to every array item it created.
 */
export const clearStoredCompositions = (documentId: string): void => {
  delete globalCompositionsStorage[documentId];
};

const EpiContext = createContext<IEpiContext | undefined>(undefined);

export const useEpiContext = () => {
  const ctx = useContext(EpiContext);
  if (!ctx) throw new Error('EpiContext not found');
  return ctx;
};

export const EpiProvider: React.FC<IEpiProviderProps> = ({
  initialCompositions,
  documentData,
  docType,
  documentId,
  tabKey,
  idmpById,
  idmpAdd,
  idmpRemove,
  epiImport,
  syncToken,
  readOnly = false,
  canManageDocuments = true,
  canRenameDocuments = true,
  canAddCustomSections = true,
  lang,
  onChange,
  children,
}) => {
  const [compositions, setCompositions] = useState(() => {
    return (
      globalCompositionsStorage[documentId]?.[tabKey] || initialCompositions
    );
  });

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleCompositions = useMemo(
    () => compositions.filter(c => !c.toBeDeleted),
    [compositions]
  );

  // save to global storage whenever compositions change
  useEffect(() => {
    if (!globalCompositionsStorage[documentId]) {
      globalCompositionsStorage[documentId] = {};
    }
    globalCompositionsStorage[documentId][tabKey] = compositions;

    saveTimeoutRef.current = setTimeout(handleSave, 500);
    // Also cancels on unmount. A provider is remounted whenever the instance
    // is refetched, and a debounce left running would write the compositions
    // it was holding — the ones just replaced — back into the page data.
    return () => clearTimeout(saveTimeoutRef.current!);
  }, [compositions, documentId, tabKey]);

  // A product leaving the ePI has to leave its composition in every tab, or
  // the subset rule fails on the next save. Whichever composition covered it
  // is found by id; if that was its only product the document goes too, since
  // a composition covering nothing is neither valid nor meaningful.
  useEffect(() => {
    if (!idmpRemove) return;
    setCompositions(comps =>
      comps.map(c => {
        if (c.toBeDeleted) return c;
        const result = removeIdmpFromComposition(c, idmpRemove.idmpId);
        return result ? result.composition : c;
      })
    );
  }, [idmpRemove?.seq]);

  // A new IDMP joining the ePI has to land in one composition per tab, or the
  // coverage rule fails on the next save. The document-level UI picks the
  // target for each tab and bumps `idmpAdd`; each provider applies its own.
  useEffect(() => {
    if (!idmpAdd) return;
    const targetId = idmpAdd.compositionIdByTab[tabKey];
    if (!targetId) return;
    setCompositions(comps =>
      comps.map(c =>
        c.id === targetId ? addIdmpsToComposition(c, [idmpAdd.idmp]) : c
      )
    );
  }, [idmpAdd?.seq]);

  // An imported document replaces this tab's documents outright. The ones it
  // replaces stay in the payload flagged rather than dropped: the BE merges
  // arrays by `id`, so a composition simply left out is kept, not deleted, and
  // the ePI would end up holding both sets.
  useEffect(() => {
    if (!epiImport) return;
    const incoming = epiImport.compositionsByTab[tabKey] ?? [];
    if (incoming.length === 0) return;
    setCompositions(comps => [
      ...comps.map(c => ({ ...c, toBeDeleted: true })),
      ...incoming,
    ]);
    // The open document and section are gone with the old ones.
    setSelectedCompositionId(incoming[0]?.id ?? null);
    setSelectedSectionPath('root');
  }, [epiImport?.seq]);

  const handleSave = useCallback(async () => {
    // Merge all tab compositions into a single document
    // May be empty for a moment after a refetch clears it — a pending debounce
    // can fire before the remounted providers have registered again.
    const allTabData = globalCompositionsStorage[documentId] ?? {};
    if (!documentData) return;

    const updatedData: EpiDocumentData = { ...documentData };
    for (const tab of TAB_CONFIGS) {
      if (allTabData[tab.key]) {
        (updatedData as any)[tab.key] = allTabData[tab.key];
      }
    }
    // Strips reference enrichment and nulls fields the schema dropped.
    onChange(normalizeForSave(updatedData));
  }, [documentId, documentData, onChange]);

  // Selection is by **id**, never by position. Soft-deleted compositions stay
  // in the array until save, so the index of a document in `compositions`
  // and its index in the rendered `visibleCompositions` drift apart the moment
  // anything is removed — and every write below would then hit the wrong one.
  const [selectedCompositionId, setSelectedCompositionId] = useState<
    string | null
  >(null);
  const composition = useMemo(
    () =>
      visibleCompositions.find(c => c.id === selectedCompositionId) ??
      visibleCompositions[0],
    [visibleCompositions, selectedCompositionId]
  );

  // Build the section tree from schema + data
  const sectionTree = useMemo(
    () =>
      composition
        ? buildCompositionSectionTree(docType, composition, lang)
        : [],
    [composition, docType, lang]
  );

  // Store only the path to avoid re-render loops (typing → setCompositions → sectionTree
  // recomputes → useEffect → setSelectedSection → re-render → TinyMCE fires onEditorChange → loop).
  const [selectedSectionPath, setSelectedSectionPath] = useState<string | null>(
    sectionTree[0]?.path ?? null
  );

  /**
   * Adopt the refetched instance. The screen reloads it after every save and
   * the server assigns its own ids to array items it created, so keeping the
   * editor's copy makes the next save append duplicates instead of merging.
   *
   * The selection is kept, since losing it on every save is the screen jumping
   * back to the top of the document. It is kept **by identity**: custom
   * sections and repeatable blocks are addressed by array index, so the same
   * path can point at a different section once the server's array differs —
   * which needs a second editor, but would otherwise silently swap what is
   * being edited. `root` and declared sections are keyed, so they compare
   * equal on the path alone.
   */
  useEffect(() => {
    if (syncToken === undefined) return;

    const idAt = (comps: EpiComposition[]) => {
      // Resolved the same way as `composition` above, fallback included, so
      // this agrees with whatever is actually on screen.
      const live = comps.filter(c => !c.toBeDeleted);
      const comp = live.find(c => c.id === selectedCompositionId) ?? live[0];
      if (!comp || !selectedSectionPath || selectedSectionPath === 'root') {
        return { found: !!comp, id: undefined as string | undefined };
      }
      const node = get(comp, selectedSectionPath) as
        | { id?: string }
        | undefined;
      return { found: node !== undefined, id: node?.id };
    };

    const before = idAt(compositions);
    const after = idAt(initialCompositions);

    setCompositions(initialCompositions);

    if (!after.found || before.id !== after.id) {
      // 'root' is the first node of every tree, so this is where a new
      // composition opens anyway.
      setSelectedSectionPath('root');
    }
    // Only a new token means the server has a copy that differs from this one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncToken]);

  // Derive the full node from the tree — no setState, no re-render cycle
  const selectedSection = useMemo(
    () =>
      selectedSectionPath
        ? findSectionNode(sectionTree, selectedSectionPath)
        : null,
    [sectionTree, selectedSectionPath]
  );

  // Read content directly from composition data to avoid stale node cache
  const selectedSectionContent = useMemo(() => {
    if (!selectedSectionPath || !composition) return '';
    if (selectedSectionPath === 'root')
      return text(composition.rootContent, lang);
    return text(get(composition, `${selectedSectionPath}.content`), lang);
  }, [composition, selectedSectionPath, lang]);

  // Public setter that accepts an EpiSectionNode (for click handlers) but stores only the path
  const setSelectedSection = useCallback((sec: EpiSectionNode | null) => {
    setSelectedSectionPath(sec?.path ?? null);
  }, []);

  const updateSectionContent = useCallback(
    (path: string, html: string) => {
      setCompositions(comps =>
        comps.map(comp => {
          if (comp.id !== composition?.id) return comp;
          if (path === 'root')
            return {
              ...comp,
              rootContent: setText(comp.rootContent, lang, html),
            };
          return updateSectionContentAtPath(comp, path, lang, html);
        })
      );
    },
    [composition?.id, lang]
  );

  const updateSectionTitle = useCallback(
    (path: string, newTitle: string) => {
      setCompositions(comps =>
        comps.map(comp => {
          if (comp.id !== composition?.id) return comp;
          if (path === 'root')
            return {
              ...comp,
              rootTitle: setText(comp.rootTitle, lang, newTitle),
            };
          return updateSectionTitleAtPath(comp, path, lang, newTitle);
        })
      );
    },
    [composition?.id, lang]
  );

  /**
   * Adds a section under `parentPath` and returns its data path, or null when
   * the key isn't in the schema. `natureType` applies to repeatable packaging
   * blocks only — it's what tells sibling instances apart.
   */
  const addOptionalSection = useCallback(
    (parentPath: string, key: string, natureType?: EpiText): string | null => {
      const keyPath = `${sectionsPath(parentPath)}.${key}`;
      const entry = getSchemaEntryAtPath(docType, keyPath);
      if (!entry) return null;
      const { def, repeatable } = resolveSectionDef(entry);

      if (repeatable) {
        // Appended, so the new instance's index is the current length —
        // soft-deleted blocks included, since they keep their slot.
        const existing = get(composition, keyPath) as unknown[] | undefined;
        const newPath = `${keyPath}[${existing?.length ?? 0}]`;
        setCompositions(comps =>
          comps.map(comp =>
            comp.id === composition?.id
              ? addPackagingBlock(
                  comp,
                  parentPath,
                  key,
                  // Same reason as addCustomSection: the position has to come
                  // from the composition as it is now, not as it was captured.
                  buildEmptyPackagingBlock(
                    def,
                    natureType,
                    nextBlockOrder(comp, parentPath)
                  )
                )
              : comp
          )
        );
        return newPath;
      }

      const newSection = buildEmptySection(def);
      setCompositions(comps =>
        comps.map(comp =>
          comp.id === composition?.id
            ? addOptionalSectionToData(comp, parentPath, key, newSection)
            : comp
        )
      );
      return keyPath;
    },
    [composition, docType]
  );

  const updateSectionNatureType = useCallback(
    (path: string, natureType: string) => {
      setCompositions(comps =>
        comps.map(comp =>
          comp.id === composition?.id
            ? updateNatureTypeAtPath(comp, path, lang, natureType)
            : comp
        )
      );
    },
    [composition?.id, lang]
  );

  const addCustomSection = useCallback(
    (parentPath: string, id: string, title: string) => {
      setCompositions(comps =>
        comps.map(comp => {
          if (comp.id !== composition?.id) return comp;
          // Built inside the updater so the print position is read from the
          // composition as it is *now*. A callback closes over the state it was
          // created with, so adding three sections in a row without an
          // intervening re-render would otherwise give all three `order: 0`.
          const customSection = {
            id,
            // Seeded in the language being edited; the other stays empty until
            // someone translates it.
            title: setText(undefined, lang, title),
            content: setText(undefined, lang, ''),
            order: nextCustomSectionOrder(comp, parentPath),
            ...getCustomSectionDefaults(docType, parentPath),
          };
          return addCustomSectionToData(comp, parentPath, customSection);
        })
      );
    },
    [composition?.id, lang, docType]
  );

  /**
   * Adds a predefined custom section under `host` and returns its data path:
   * the revived item's, when the entry was deleted earlier in this session.
   */
  const addPredefinedSection = useCallback(
    (host: EpiSectionNode, entry: EpiPredefinedEntry): string => {
      const existing = (get(composition, host.path)?.customSections ??
        []) as EpiCustomSection[];
      const revived = existing.findIndex(
        c => c.toBeDeleted && c.predefinedKey === entry.key
      );
      const index = revived < 0 ? existing.length : revived;
      const section: EpiCustomSection = {
        id: uuid(),
        predefinedKey: entry.key,
        // Both languages: the entry knows both, and the title is locked.
        title: { ...emptyText(), ...entry.title },
        content: emptyText(),
        order: 0,
        ...(entry.afterSequenceOrder !== undefined
          ? { afterSequenceOrder: entry.afterSequenceOrder }
          : {}),
        ...getCustomSectionDefaults(docType, host.path),
      };
      const entries = getPredefinedEntries(docType, host);
      setCompositions(comps =>
        comps.map(comp =>
          comp.id === composition?.id
            ? addPredefinedSectionToData(comp, host.path, entries, section)
            : comp
        )
      );
      return `${host.path}.customSections[${index}]`;
    },
    [composition, docType]
  );

  const keepCustomSection = useCallback(
    (path: string) => {
      setCompositions(comps =>
        comps.map(comp =>
          comp.id === composition?.id
            ? keepCustomSectionInData(comp, path)
            : comp
        )
      );
    },
    [composition?.id]
  );

  /**
   * Adds a block of `key` printing right after the block at `blockPath`, and
   * returns its data path. Only for blocks without a nature/type — nobody is
   * asked for one here.
   */
  const addPackagingBlockAfter = useCallback(
    (blockPath: string, key: string): string | null => {
      const parentPath =
        blockPath.replace(/\.?sections\.[^.]+\[\d+\]$/, '') || 'root';
      const keyPath = `${sectionsPath(parentPath)}.${key}`;
      const entry = getSchemaEntryAtPath(docType, keyPath);
      const after = get(composition, blockPath) as
        | EpiPackagingBlock
        | undefined;
      if (!entry || !after) return null;
      const existing = get(composition, keyPath) as unknown[] | undefined;
      const block = buildEmptyPackagingBlock(resolveSectionDef(entry).def);
      setCompositions(comps =>
        comps.map(comp =>
          comp.id === composition?.id
            ? insertPackagingBlockAfter(
                comp,
                parentPath,
                key,
                block,
                after.order ?? 0
              )
            : comp
        )
      );
      return `${keyPath}[${existing?.length ?? 0}]`;
    },
    [composition, docType]
  );

  const removeSection = useCallback(
    (path: string, isCustom: boolean, customSectionId?: string) => {
      setCompositions(comps =>
        comps.map(comp => {
          if (comp.id !== composition?.id) return comp;
          if (isCustom && customSectionId) {
            // path is e.g. "sections.composition.customSections[0]"
            // strip "customSections[N]" to get the parent section path
            const parentPath = path.replace(/\.customSections\[\d+\]$/, '');
            return removeCustomSection(comp, parentPath, customSectionId);
          } else if (isBlockPath(path)) {
            // One instance of a repeatable section — soft-delete the array
            // item, don't drop the key.
            return removePackagingBlock(comp, path);
          } else {
            // path is like "sections.clinicalParticulars.sections.paediatricPopulation"
            // parentPath = "sections.clinicalParticulars.sections", key = last part
            const parts = path.split('.');
            const parentPath = parts.slice(0, -1).join('.');
            const sectionKey = parts[parts.length - 1];
            return removeOptionalSection(comp, parentPath, sectionKey);
          }
        })
      );
    },
    [composition?.id]
  );

  // Position is structure, shared by both languages, so the language plays no
  // part. Computed inside the updater, from the composition as it is now.
  const moveCustomSection = useCallback(
    (path: string, direction: -1 | 1) => {
      setCompositions(comps =>
        comps.map(comp =>
          comp.id === composition?.id
            ? moveCustomSectionInData(comp, path, direction)
            : comp
        )
      );
    },
    [composition?.id]
  );

  /**
   * Peels IDMPs off a composition into a new one. `duplicate` copies the
   * source's sections, `fresh` starts from the empty required-key skeleton.
   * Either way the peeled IDMPs leave the source, so the partition holds.
   *
   * The caller guarantees the selection is non-empty and leaves at least one
   * IDMP behind — both would otherwise break the invariant.
   */
  const peelIntoNewComposition = useCallback(
    (
      sourceId: string,
      peeledIdmpUuids: string[],
      mode: 'duplicate' | 'fresh'
    ) => {
      if (peeledIdmpUuids.length === 0) return;
      let newId: string | null = null;
      setCompositions(comps => {
        const sourceIdx = comps.findIndex(c => c.id === sourceId);
        const source = comps[sourceIdx];
        if (!source) return comps;

        const titles = comps.map(c => text(c.title, lang));
        // Appended, so it prints last until someone reorders.
        const nextOrder =
          Math.max(-1, ...comps.filter(c => !c.toBeDeleted).map(c => c.order)) +
          1;
        let newComp: EpiComposition;
        if (mode === 'duplicate') {
          newComp = cloneCompositionForIdmps(
            source,
            peeledIdmpUuids,
            nextOrder
          );
          newComp.title = setText(
            source.title,
            lang,
            generateCopyTitle(text(source.title, lang), titles)
          );
        } else {
          newComp = buildEmptyComposition(
            docType,
            pickIdmpRefs(source, peeledIdmpUuids),
            undefined,
            nextOrder
          );
          // A fresh document isn't a copy of anything — it keeps the name
          // its type carries in the schema, disambiguated only on a clash.
          // Each language is made unique against its own siblings, so the two
          // cannot drift apart at birth.
          newComp.title = {
            sr: uniqueTitle(
              text(newComp.title, 'sr'),
              comps.map(c => text(c.title, 'sr'))
            ),
            en: uniqueTitle(
              text(newComp.title, 'en'),
              comps.map(c => text(c.title, 'en'))
            ),
          };
        }

        const next = comps.slice();
        next[sourceIdx] = peelIdmpsFromComposition(source, peeledIdmpUuids);
        next.push(newComp);
        newId = newComp.id;
        return next;
      });
      if (newId) setSelectedCompositionId(newId);
    },
    [docType, lang]
  );

  const renameComposition = useCallback(
    (newTitle: string) => {
      if (isTitleTaken(compositions, composition?.id, newTitle, lang)) {
        message.error('A document with this title already exists.');
        return;
      }
      setCompositions(comps =>
        comps.map(comp =>
          comp.id === composition?.id
            ? { ...comp, title: setText(comp.title, lang, newTitle) }
            : comp
        )
      );
    },
    [compositions, composition?.id, lang]
  );

  /**
   * Removes a composition by merging it into a sibling: the source's IDMPs
   * transfer so they stay covered, and the source is soft-deleted. The
   * source's sections and title are lost — the caller confirms that first.
   */
  const mergeCompositionIntoSibling = useCallback(
    (sourceId: string, targetId: string) => {
      if (sourceId === targetId) return;
      setCompositions(comps => {
        const sourceIdx = comps.findIndex(c => c.id === sourceId);
        const targetIdx = comps.findIndex(c => c.id === targetId);
        const source = comps[sourceIdx];
        const target = comps[targetIdx];
        if (!source || !target) return comps;
        const result = mergeCompositionIntoSiblingOp(source, target);
        if (!result) return comps;
        const next = comps.slice();
        next[sourceIdx] = result.softDeletedSource;
        next[targetIdx] = result.updatedTarget;
        return next;
      });
      setSelectedCompositionId(targetId);
    },
    []
  );

  return (
    <EpiContext.Provider
      value={{
        compositions,
        visibleCompositions,
        readOnly,
        canManageDocuments,
        canRenameDocuments,
        canAddCustomSections,
        selectedCompositionId: composition?.id ?? null,
        composition,
        documentData,
        selectedSection,
        selectedSectionContent,
        sectionTree,

        setSelectedComposition: setSelectedCompositionId,
        setSelectedSection,

        updateSectionContent,
        updateSectionTitle,

        addOptionalSection,
        updateSectionNatureType,
        addCustomSection,
        addPredefinedSection,
        keepCustomSection,
        addPackagingBlockAfter,
        removeSection,
        moveCustomSection,

        peelIntoNewComposition,
        renameComposition,
        mergeCompositionIntoSibling,
        idmpById,
        lang,
      }}
    >
      {children}
    </EpiContext.Provider>
  );
};

/**
 * Generate a new title with a " – Copy" suffix, incrementing existing copies.
 */
/** Appends a counter only when the base title is already taken. */
function uniqueTitle(base: string, taken: string[]): string {
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(`${base} (${n})`)) n++;
  return `${base} (${n})`;
}

function generateCopyTitle(original: string, allTitles: string[]): string {
  const root = original.replace(/ – Copy(?: \(\d+\))?$/, '');
  const esc = root.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  const regex = new RegExp(`^${esc} – Copy(?: \\((\\d+)\\))?$`);
  let max = 0;

  allTitles.forEach(t => {
    const m = t.match(regex);
    if (m) {
      const idx = m[1] ? parseInt(m[1], 10) : 1;
      max = Math.max(max, idx);
    }
  });

  const next = max + 1;
  return next === 1 ? `${root} – Copy` : `${root} – Copy (${next})`;
}
